import { useRef, useState } from "react";
import { Camera, Loader2, Paperclip, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { fileUrl, uploadFile, useFileMeta, useFileUrl } from "@/lib/files";

export function FileImage({ id, className = "", alt = "" }) {
  const meta = useFileMeta(id);
  return meta?.type.startsWith("image/") ? <img src={meta.url} alt={alt} className={className} data-testid="file-image" /> : null;
}

function useUpload(kind, onChange) {
  const { t } = useApp();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!navigator.onLine) { toast.error(t("upload_offline")); return; }
    setBusy(true);
    try { const r = await uploadFile(f, kind); onChange(r.id); toast.success(t("upload_done")); } catch (err) { toast.error(err.response?.data?.detail || t("upload_failed")); } finally { setBusy(false); }
  };
  return [busy, pick];
}

export function PhotoButton({ kind = "product", onChange, label, testid = "photo-btn", className = "" }) {
  const input = useRef(null);
  const [busy, pick] = useUpload(kind, onChange);
  return (
    <>
      <button type="button" onClick={() => input.current?.click()} disabled={busy} className={`h-8 px-2 rounded border border-dashed border-[#1F4E8C] text-[#1F4E8C] text-[11px] font-bold flex items-center gap-1 ${className}`} data-testid={testid}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}{label}</button>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={pick} data-testid={`${testid}-input`} />
    </>
  );
}

export function ImagePicker({ value, onChange, kind = "product", testid = "image-picker" }) {
  const { t } = useApp();
  const input = useRef(null);
  const [busy, pick] = useUpload(kind, onChange);
  const url = useFileUrl(value);
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`${testid}-btn`}
        className="w-20 h-20 shrink-0 rounded-2xl border-2 border-dashed border-border bg-secondary grid place-items-center overflow-hidden hover:border-primary transition-colors">
        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : url ? <img src={url} alt="" className={`w-full h-full ${["signature", "stamp", "logo"].includes(kind) ? "object-contain bg-white" : "object-cover"}`} data-testid={`${testid}-preview`} /> : <Camera className="w-7 h-7 text-muted-foreground" />}
      </button>
      <div className="text-sm">
        <div className="font-bold">{kind === "logo" ? t("shop_logo") : kind === "staff" ? t("staff_photo") : kind === "signature" ? t("signature_image") : kind === "stamp" ? t("stamp_image") : t("photo")}</div>
        <div className="text-muted-foreground text-xs">{t("photo_hint")}</div>
        {value && <button type="button" onClick={() => onChange("")} className="text-destructive text-xs font-bold mt-1 flex items-center gap-1" data-testid={`${testid}-remove`}><X className="w-3 h-3" />{t("remove_photo")}</button>}
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={pick} data-testid={`${testid}-input`} />
    </div>
  );
}

export const openFile = async (id) => { try { window.open(await fileUrl(id), "_blank"); } catch (e) { toast.error("Error"); } };

export function AttachmentField({ value, onChange, testid = "attachment" }) {
  const { t } = useApp();
  const input = useRef(null);
  const [busy, pick] = useUpload("attachment", onChange);
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`${testid}-btn`}
        className="h-12 px-4 rounded-xl border border-border bg-white font-bold flex items-center gap-2 hover:border-primary transition-colors">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}{value ? t("replace_attachment") : t("attach_invoice")}
      </button>
      {value && (
        <>
          <button type="button" onClick={() => openFile(value)} className="h-12 px-3 rounded-xl bg-[#E3F6EC] text-[#1F9D55] font-bold flex items-center gap-1.5" data-testid={`${testid}-view`}><Eye className="w-4 h-4" />{t("view")}</button>
          <button type="button" onClick={() => onChange("")} className="h-12 w-12 rounded-xl bg-destructive/10 text-destructive grid place-items-center" aria-label={t("remove_photo")} data-testid={`${testid}-remove`}><X className="w-4 h-4" /></button>
        </>
      )}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" hidden onChange={pick} data-testid={`${testid}-input`} />
    </div>
  );
}
